/**
 * LLD-SPA-177 — "tokens are a TypeScript union, not a convention." A value outside §33.9 must be
 * a compile error, not a review comment. This module is the sanctioned way code reaches a token
 * by name (an inline style, a dynamically-chosen class); every literal here is transcribed from
 * process/11-TOKEN-MAP.md and src/styles/tokens.css, and the two are checked against each other by
 * `token-values-match.test.ts` — this file does not re-decide a value, it re-types one.
 *
 * Tailwind utility classes (`text-body`, `p-4`, …) are the other sanctioned path and are typed by
 * Tailwind's own generated class list via tailwind.config.ts; the ESLint rule
 * `token-literal-forbidden` is the backstop that catches a raw literal or an arbitrary-value
 * Tailwind class (`text-[13px]`) bypassing both.
 */

export type FontSizeToken =
  | 'fs-micro'
  | 'fs-small'
  | 'fs-body'
  | 'fs-body-lg'
  | 'fs-heading'
  | 'fs-section'
  | 'fs-display'

export type SpaceToken =
  | 'sp-0'
  | 'sp-1'
  | 'sp-2'
  | 'sp-3'
  | 'sp-4'
  | 'sp-5'
  | 'sp-6'
  | 'sp-7'
  | 'sp-8'
  | 'sp-9'
  | 'sp-10'
  | 'sp-11'

export type RadiusToken = 'r-0' | 'r-1' | 'r-2' | 'r-3' | 'r-4' | 'r-pill'

export type WeightToken = 'fw-normal' | 'fw-semibold' | 'fw-bold' | 'fw-heavy'

export type LeadingToken = 'lh-tight' | 'lh-normal' | 'lh-relaxed' | 'lh-loose'

export type ShadowToken = 'shadow-1' | 'shadow-2' | 'shadow-3' | 'shadow-focus'

export type FontFamilyToken = 'font-family' | 'font-family-mono'

export type GradientToken = 'gradient-canvas' | 'gradient-topbar' | 'gradient-card'

export type ColorToken =
  | 'blue'
  | 'blue-hover'
  | 'link'
  | 'chip-bg'
  | 'chip-tx'
  | 'green'
  | 'teal'
  | 'indigo'
  | 'magenta'
  | 'orange'
  | 'slate'
  | 'pill-ok'
  | 'ok'
  | 'warn'
  | 'fail'
  | 'info'
  | 'nochange'
  | 'danger-bd'
  | 'danger-bg'
  | 'warn-bd'
  | 'warn-bg'
  | 'info-bd'
  | 'info-bg'
  | 'tx'
  | 'tx2'
  | 'muted'
  | 'bd'
  | 'bd2'
  | 'row'
  | 'panel'
  | 'strip'
  | 'sel'
  | 'accent-blue'
  | 'accent-green'
  | 'accent-teal'
  | 'accent-indigo'
  | 'accent-danger'
  | 'accent-slate'
  | 'chip-active-bg'
  | 'lozenge-type-tx'

export type Token =
  | FontSizeToken
  | SpaceToken
  | RadiusToken
  | WeightToken
  | LeadingToken
  | ShadowToken
  | FontFamilyToken
  | GradientToken
  | ColorToken

/** `cssVar('sp-4')` → `"var(--sp-4)"`. The parameter type is the whole enforcement mechanism: a
 * name outside the union above fails `tsc`, not a review. */
export function cssVar<T extends Token>(token: T): `var(--${T})` {
  return `var(--${token})`
}
